import { useState } from 'react'
import { useMediaQuery } from '@mui/material'
import { ArrowUUpLeftIcon, InfoIcon, RobotIcon } from '@phosphor-icons/react'
import { format } from 'date-fns'
import { useQuery } from '@tanstack/react-query'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { Breadcrumb } from '@components/shared/Breadcrumb'
import { Button } from '@components/shared/Button'
import { DownloadIcon } from '@assets/svgs/DownloadIcon'
import { downloadBlobFile } from '@utils/functions/downloadBlobFile'
import { emotionTheme } from '@styles/theme'
import { initialPagination } from '@components/shared/Table/components/Pagination/types'
import { Table } from '@components/shared/Table'
import { toast } from '@utils/functions/notificationToast'
import { Tooltip } from '@components/shared/Tooltip'
import { usePreview } from '@pages/Preview/context'
import { useUser } from '@hooks/useUser'

import { generateExcelFileName } from '../../helpers/generateExcelFileName'
import { buildPreviewHistoryPayload } from './helpers/buildPreviewHistoryPayload'
import { HistoryFilters } from './components/HistoryFilters'
import { parsePreviewHistoryGrid } from './helpers/parsePreviewHistoryGrid'

import type { SortProps } from '@components/shared/Table/types'
import type { PreviewHistoryExtraFiltersFormProps } from './components/HistoryFilters/types'
import type { PreviewHistoryModalProps } from './types'
import type { PreviewManagementFiltersFormProps } from '../PreviewManagementFilters/form/types'

import * as apiPreviewManagement from '@services/api/previewManagement'
import * as S from './styles'

const RETURNED_TOOLTIP = (
  <>
    <b>Sobre Devolvidas</b>
    <br />
    Contabiliza cada atualização com status: Devolvida GU, Devolvida Controller e Devolvida Head Fin
    <br />
    A contagem considera todos os filtros aplicados, exceto o filtro <b>Status</b>.
    <br />
    Uma mesma prévia pode possuir múltiplas devoluções contabilizadas.
  </>
)

const AUTO_SEND_TOOLTIP = (
  <>
    <b>Sobre Envios Automáticos</b>
    <br />
    Contabiliza cada atualização identificada como envio automático.
    <br />
    A contagem considera todos os filtros aplicados, exceto o filtro <b>Envio Automático</b>.
    <br />
    Atualizações repetidas de uma mesma prévia também são contabilizadas.
  </>
)

export const PreviewHistoryModal = ({ open, onClose, initialFilters }: PreviewHistoryModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)
  const { periodSelectedData } = usePreview()
  const { filiData } = useUser()

  const [currentPage, setCurrentPage] = useState<number>(initialPagination.PAGE)
  const [itemsPerPage, setItemsPerPage] = useState<number>(initialPagination.ITEMS_PER_PAGE)
  const [sort, setSort] = useState<SortProps | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [baseFilters, setBaseFilters] = useState<PreviewManagementFiltersFormProps | null>(null)
  const [extraFilters, setExtraFilters] = useState<PreviewHistoryExtraFiltersFormProps | null>(null)

  const referenceDate = periodSelectedData?.competenceDate

  const payload = buildPreviewHistoryPayload({
    referenceDate: referenceDate!,
    branchCode: filiData?.filiCode,
    baseFilters: baseFilters ?? initialFilters ?? null,
    extraFilters,
    sort,
    currentPage,
    itemsPerPage
  })

  const { data: historyData, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ['preview-status-history', payload],
    queryFn: () => apiPreviewManagement.searchPreviewStatusHistory(payload),
    enabled: open && !!referenceDate
  })

  // Cada abertura começa dos filtros da tela; o que foi filtrado dentro do modal não fica salvo
  const handleClose = () => {
    setBaseFilters(null)
    setExtraFilters(null)
    setSort(null)
    setCurrentPage(initialPagination.PAGE)
    onClose()
  }

  const handleExport = async () => {
    setIsExporting(true)

    try {
      const blob = await apiPreviewManagement.exportPreviewStatusHistory(payload)
      downloadBlobFile(blob, generateExcelFileName('HistoricoPrevias'))
      toast.success('Exportação do Histórico de Prévias realizada com sucesso!')
    } catch (error) {
      toast.error('Não foi possível exportar o Histórico de Prévias. Tente novamente.')
    } finally {
      setIsExporting(false)
    }
  }

  const rows = historyData?.items ?? []
  // RF05/RF06: totais da consulta inteira vindos do back; sem indicador, exibe zero
  const returnedCount = historyData?.indicators?.returnedCount ?? 0
  const autoSendCount = historyData?.indicators?.automaticSendCount ?? 0

  const { data, totalData } = parsePreviewHistoryGrid(rows, historyData?.totalItems ?? rows.length)

  return (
    <BaseModal2
      open={open}
      onClose={handleClose}
      width={isMobile ? 'calc(100% - 20px)' : '96.5%'}
      heightContainer="auto"
      maxHeightContainer={isMobile ? 'calc(100% - 20px)' : '95%'}
      headerIcon={
        <Breadcrumb
          breadcrumbData={[
            { label: 'Gestão Financeira' },
            { label: 'Prévia' },
            { label: 'Gestão da Prévia' },
            { label: 'Histórico de Prévias' }
          ]}
        />
      }
      footer={{ cancelButtonText: 'Voltar', cancelButtonMinWidth: 120 }}
    >
      <S.Header>
        <S.HeaderTitle>Histórico de Prévias</S.HeaderTitle>
        {!!periodSelectedData?.competencePeriod?.label && (
          <S.CompetenceBadge>
            Competência: {periodSelectedData.competencePeriod.label}
            {referenceDate && `/${referenceDate.slice(2, 4)}`}
          </S.CompetenceBadge>
        )}
      </S.Header>

      <S.SummaryCards>
        <S.SummaryCard variant="returned">
          <S.SummaryCardHeader>
            <S.SummaryCardTitle>
              Devolvidas
              <Tooltip title={RETURNED_TOOLTIP} placement="top" contentWidth="auto">
                <InfoIcon size={14} />
              </Tooltip>
            </S.SummaryCardTitle>
            <S.SummaryCardIcon variant="returned">
              <ArrowUUpLeftIcon size={18} />
            </S.SummaryCardIcon>
          </S.SummaryCardHeader>
          <S.SummaryCardValue>{returnedCount.toLocaleString('pt-BR')}</S.SummaryCardValue>
          <S.SummaryCardDescription>Atualizações com devolução</S.SummaryCardDescription>
        </S.SummaryCard>

        <S.SummaryCard variant="autoSend">
          <S.SummaryCardHeader>
            <S.SummaryCardTitle>
              Envios Automáticos
              <Tooltip title={AUTO_SEND_TOOLTIP} placement="top" contentWidth="auto">
                <InfoIcon size={14} />
              </Tooltip>
            </S.SummaryCardTitle>
            <S.SummaryCardIcon variant="autoSend">
              <RobotIcon size={18} />
            </S.SummaryCardIcon>
          </S.SummaryCardHeader>
          <S.SummaryCardValue>{autoSendCount.toLocaleString('pt-BR')}</S.SummaryCardValue>
          <S.SummaryCardDescription>Atualizações via automação</S.SummaryCardDescription>
        </S.SummaryCard>
      </S.SummaryCards>

      <HistoryFilters
        initialBaseFilters={initialFilters}
        onFilter={(base, extra) => {
          setBaseFilters(base)
          setExtraFilters(extra)
          setCurrentPage(initialPagination.PAGE)
        }}
      />

      <S.Divider />

      <S.TableToolbar>
        <S.UpdatedAt>
          {!!dataUpdatedAt && `Atualizado em ${format(dataUpdatedAt, "dd/MM/yyyy 'às' HH:mm:ss")}`}
        </S.UpdatedAt>
        <Button
          colorType="outlineBlue"
          width="auto"
          rightIcon={<DownloadIcon />}
          onClick={handleExport}
          loading={isExporting}
          disabled={isExporting || !referenceDate}
        >
          Exportar
        </Button>
      </S.TableToolbar>

      <Table
        data={data}
        totalData={totalData}
        isLoading={isLoading}
        onSort={(sortData) => {
          setSort(sortData)
          setCurrentPage(initialPagination.PAGE)
        }}
        footer={{
          pagination: {
            currentPage: historyData?.currentPage ?? currentPage,
            itemsPerPage: historyData?.itemsPerPage ?? itemsPerPage,
            totalItems: historyData?.totalItems,
            totalPages: historyData?.totalPages,
            onPageChange: setCurrentPage,
            onItemsPerPageChange: (value) => {
              setItemsPerPage(value)
              setCurrentPage(initialPagination.PAGE)
            }
          }
        }}
      />
    </BaseModal2>
  )
}
